import { useState } from "react"
import EditZonePolicyPage from "@/routes/_home/zone-settings/zone-policy-due-date/edit"
import { ZonePolicyDueDateType } from "@/types"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { validateUserDeletePermission } from "@/lib/api/auth"
import { deleteZonePolicyDue } from "@/lib/api/billing"
import { Button } from "@/components/ui/button"
import {
  Credenza,
  CredenzaBody,
  CredenzaClose,
  CredenzaContent,
  CredenzaDescription,
  CredenzaFooter,
  CredenzaHeader,
  CredenzaTitle,
} from "@/components/ui/credenza"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Icons } from "@/components/icons"

interface DataProps {
  data: ZonePolicyDueDateType
}

export function ZonesPolicyOperation({ data }: DataProps) {
  const [showDropDown, setShowDropDown] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: deleteZonePolicyDue,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["policyDetails"] })
    },
    onError: () => {
      alert("Error deleting item")
    },
  })

  return (
    <div>
      <DropdownMenu open={showDropDown} onOpenChange={setShowDropDown}>
        <DropdownMenuTrigger>
          <div className="flex h-8 w-8 items-center justify-center rounded-md border transition-colors hover:bg-muted">
            <Icons.ellipsis className="size-4" />
            <span className="sr-only">Open</span>
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onSelect={(event) => {
              event.preventDefault() // Prevent dropdown from closing immediately
              setShowEditModal(true)
              setShowDropDown(false)
            }}
          >
            <Icons.edit className="mr-2 size-4" />
            Edit Zone Policy
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem>
            <Icons.copy className="mr-2 size-4" />
            Copy Zone Policy
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem>
            <Icons.paste className="mr-2 size-4" />
            Paste Zone Policy
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-destructive"
            onSelect={(event) => {
              event.preventDefault()
              setShowDeleteDialog(true)
              setShowDropDown(false)
            }}
          >
            <Icons.archive className="mr-2 size-4" />
            Archive
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Delete Confirmation Dialog */}
      <Credenza open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <CredenzaContent>
          <CredenzaHeader>
            <CredenzaTitle>Confirm Deletion</CredenzaTitle>
          </CredenzaHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete Id {data.id}?
          </p>
          <CredenzaFooter>
            <Button
              variant="destructive"
              onClick={() => {
                if (!validateUserDeletePermission()) return

                if (data.id !== undefined) {
                  mutation.mutate(data.id)
                } else {
                  console.error("Error: data.id is undefined")
                }

                setShowDeleteDialog(false)
              }}
            >
              Delete
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
            >
              Cancel
            </Button>
          </CredenzaFooter>
        </CredenzaContent>
      </Credenza>

      <Credenza open={showEditModal} onOpenChange={setShowEditModal}>
        <EditZonePolicyPage
          zonePolicyData={{ ...data }}
          setShowEditModal={setShowEditModal}
        />
      </Credenza>
    </div>
  )
}
